import React, { useMemo } from 'react';
import {
  ProductionCard,
  ProductionStats,
  ProductionValue,
  StatBlock,
  StyledDescText,
} from './ProductionOrderV2.styles';
import {
  AllieDotIcon,
  AllieTimeQuarterPastIcon,
  AlliePinUnmarked,
  AllieBarChartOutlineIcon,
  AllieTargetIcon,
  AllieCubeOutlineIcon,
} from '../../../styling/icons';
import { OrderStatus } from './components/ToolbarButtons';
import {
  POProgressBar,
  InfoOP,
  AllieChipStyled,
  HeaderCard,
  TitleCard,
  StyledDescTextTV,
} from './ProductionOrder.styles';
import { AllieText } from '../../../components/Text';
import { AllieEllipsedText } from '../EllipsedText';
import { MulticolorProgressBar } from '../../../components/ProgressIndicators/MulticolorBar';
import { getColorBasedOnThreshold } from '../../../common/utils';
import { AllieColors } from '../../../common/color.types';
import { IconData, IconTVData } from './components/IconData';

interface ProductionProgressProps {
  op: string;
  status: string;
  product: string;
  currentProduction: string;
  estimatedProduction: string;
  progress: number;
  startDate?: string;
  produced?: string;
  accumulatedProduced?: string;
  estimated?: string;
  unit?: string;
  labels: {
    status: string;
    statusDone: string;
    statusPaused: string;
    shiftProduction: string;
    estimatedProduction: string;
    progress: string;
    productionOrder: string;
    begin: string;
    produced: string;
    accumulatedProductionShift: string;
    estimatedOPShift: string;
  };
  redThreshold?: number;
  greenThreshold?: number;
}

export const ProductionOrderTvV3: React.FC<ProductionProgressProps> = ({
  op,
  status,
  product,
  currentProduction,
  estimatedProduction,
  labels,
  progress,
  redThreshold,
  greenThreshold,
  startDate,
  produced,
  accumulatedProduced,
  estimated,
  unit,
}) => {
  const mapProductionStatusChipBorder: Record<string, string> = {
    in_progress: '1.5px solid var(--allie-color-primary-60)',
    done: '1.5px solid var(--allie-color-neutral-60)',
    paused: '1.5px solid var(--allie-color-neutral-60)',
  };
  const mapProductionStatusChipColor: Record<string, string> = {
    in_progress: 'var(--allie-color-neutral-main)',
    done: 'var(--allie-color-neutral-70)',
    paused: 'var(--allie-color-neutral-70)',
  };
  const mapStatusLabel: Record<string, string> = {
    in_progress: labels.status,
    done: labels.statusDone,
    paused: labels.statusPaused,
  };

  const progressColor = useMemo(
    () =>
      redThreshold || greenThreshold
        ? (getColorBasedOnThreshold(
            (progress ?? 0) * 100,
            redThreshold,
            greenThreshold
          ) as AllieColors)
        : 'primary-60',
    [progress, redThreshold, greenThreshold]
  );

  return (
    <ProductionCard>
      <HeaderCard>
        <TitleCard style={{ justifyContent: 'space-between' }}>
          <AllieText color="neutral-90" type="h3" weight={600} style={{ flexShrink: 0 }}>
            {`${labels.productionOrder} ${op ?? ' -'}`}
          </AllieText>
          {product && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
              <AllieCubeOutlineIcon />
              <AllieText
                color="neutral-90"
                type="body1"
                weight={400}
                lineClamp={1}
                style={{ whiteSpace: 'nowrap', maxWidth: 400 }}
              >
                {product}
              </AllieText>
            </div>
          )}
          {status && (
            <AllieChipStyled status={status} style={{ flexShrink: 0, height: '36px' }}>
              {status === OrderStatus.InProgress && <AllieDotIcon />}
              <StyledDescTextTV
                type="h5"
                style={{
                  color: mapProductionStatusChipColor[status],
                }}
                weight={600}
              >
                {mapStatusLabel[status]}
              </StyledDescTextTV>
            </AllieChipStyled>
          )}
        </TitleCard>
      </HeaderCard>

      <InfoOP style={{ margin: '8px 0 0' }}>
        <IconTVData
          text={labels.begin}
          icon={<AllieTimeQuarterPastIcon display="contents" />}
          value={startDate || '-'}
          fontType="h5"
        />
        <IconTVData
          text={labels.produced}
          icon={<AlliePinUnmarked display="contents" />}
          value={produced ? `${produced} ${unit}` : '-'}
          fontType="h5"
        />
      </InfoOP>

      <InfoOP style={{ margin: '0 0 8px' }}>
        <IconTVData
          text={labels.accumulatedProductionShift}
          icon={<AllieBarChartOutlineIcon display="contents" />}
          value={accumulatedProduced ? `${accumulatedProduced} ${unit}` : '-'}
          fontType="h5"
        />
        <IconTVData
          text={labels.estimatedOPShift}
          icon={<AllieTargetIcon display="contents" allieColor="neutral-70" />}
          value={estimated}
          fontType="h5"
        />
      </InfoOP>

      <ProductionStats style={{ margin: '0 0 12px' }}>
        <StatBlock>
          <AllieText type="h5" weight={500} color="neutral-60">
            {labels.shiftProduction}
          </AllieText>
          <ProductionValue>{currentProduction}</ProductionValue>
        </StatBlock>
        <StatBlock>
          <AllieText type="h5" weight={500} color="neutral-60">
            {labels.estimatedProduction}
          </AllieText>
          <ProductionValue>{estimatedProduction}</ProductionValue>
        </StatBlock>
      </ProductionStats>

      <POProgressBar isTv style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 10px',
            height: '100%',
            position: 'absolute',
            width: '100%',
            zIndex: 1,
          }}
        >
          <AllieEllipsedText
            textProps={{
              type: 'body2',
              color: `${progress && progress <= 0.65 ? 'black-90' : 'white'}`,
              weight: 500,
            }}
            value={
              progress !== undefined && progress > 0.1 && labels.progress
                ? `${labels.progress}: ${Math.round((progress || 0) * 100)}%`
                : ''
            }
            triggerId={'planned-production'}
            maxWidth={'auto'}
          />
        </div>
        <MulticolorProgressBar
          values={[{ color: progressColor, value: (progress ?? 0) * 100 }]}
          height={'100%'}
          borderRadius={100}
          weightRemainingText={600}
          remainingText={
            progress !== undefined && progress <= 0.1
              ? `${Math.round((progress || 0) * 100)}% ${labels.progress}`
              : undefined
          }
        />
      </POProgressBar>
    </ProductionCard>
  );
};
